import Location from "@components/Location/index";
import MapPopup from "@components/MapPopup";
import { useFormContext } from "@hudson/ui/components/ReactHookForm";
import Session from "@lib/session";
import useContent from "@lib/use_content";
import useWindowSize from "@utils/use_window_size";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";
import { SyntheticEvent, useEffect } from "react";
import SwapButton from "./SwapButton";

interface PickupAndDropoffProps {
  className?: string;
  fieldNames: { pickup: string; dropoff: string };
  findLocations?: be.FindLocationsFunction;
  limitLocations?: number;
  locationPredictionRenderer?: JSX.Element;
  locationPredictionSelected?: be.LocationPredictionSelectedFunction;
  noMap?: boolean;
}

function PickupAndDropoff({
  className,
  fieldNames,
  findLocations,
  limitLocations,
  locationPredictionRenderer,
  locationPredictionSelected,
  noMap = false,
}: PickupAndDropoffProps): JSX.Element | null {
  const { data, error, loading } = useContent([
    "beErrFieldRequired",
    "bePickupLocLabel",
    "beMapPopupLinkLabel",
    "beDropoffLocLabel",
  ]);
  const [size] = useWindowSize();

  // Hook input into form
  const { clearErrors, setValue, watch } = useFormContext();
  const [pickup, dropoff] = watch([fieldNames.pickup, fieldNames.dropoff]);

  // Try to keep the booking-level service area in sync with the current state of
  // the location selection. This ensures that any service-area-dependant error messages
  // or other content can be as accurate as possible.
  useEffect(() => {
    let serviceArea = Session.getConfig().serviceArea;
    if (typeof pickup?.serviceArea === "number") serviceArea = pickup.serviceArea;
    else if (typeof dropoff?.serviceArea === "number") serviceArea = dropoff.serviceArea;
    else if (typeof pickup?.serviceArea === "object" && typeof dropoff?.serviceArea === "object") {
      // Both pickup and dropoff have service area lists. Pick one of the overlapping areas.
      let intersection = pickup.serviceArea.filter((x: number[]) =>
        dropoff.serviceArea.includes(x),
      );
      if (intersection.length > 0) serviceArea = intersection[0];
    }

    if (serviceArea && Session.getBooking().serviceArea != serviceArea) {
      Session.updateBooking({ serviceArea });
      global.window.apolloClient.stop();
      global.window.apolloClient.resetStore();
    }
  }, [pickup, dropoff]);

  if (error) {
    console.error(error);
    return null;
  }

  const swap = function (e: SyntheticEvent) {
    e.preventDefault();
    setValue(fieldNames.pickup, dropoff, { shouldDirty: !!dropoff, shouldValidate: !!dropoff });
    if (!dropoff) clearErrors(fieldNames.pickup); // Clear errors if we're swapping to an empty field
    setValue(fieldNames.dropoff, pickup, { shouldDirty: !!pickup, shouldValidate: !!pickup });
    if (!pickup) clearErrors(fieldNames.dropoff); // Clear errors if we're swapping to an empty field
  };

  const locationObj = function (
    direction: "pickup" | "dropoff",
    { className = "", placeholder = "" }: { className?: string; placeholder?: string },
  ) {
    const obj = direction == "pickup" ? pickup : dropoff;
    const otherObj = direction == "pickup" ? dropoff : pickup;
    const serviceAreaFilter =
      direction == "dropoff"
        ? Session.getConfig().serviceArea || pickup?.serviceArea
        : Session.getConfig().serviceArea;

    return (
      <Location
        className={className}
        fieldName={fieldNames[direction]}
        findLocations={findLocations}
        limitLocations={limitLocations}
        locationPredictionRenderer={locationPredictionRenderer}
        locationPredictionSelected={locationPredictionSelected}
        placeholder={placeholder}
        searchCenter={otherObj?.latLng?.lng ? otherObj.latLng : undefined}
        serviceAreaFilter={serviceAreaFilter}
      />
    );
  };

  return (
    <motion.div
      layout
      transition={{
        type: "tween",
        ease: "easeOut",
        duration: 0.2,
      }}
      className={`${
        className ? className : ""
      } relative flex flex-col flex-1 normal:flex-1 h-full `}
    >
      <div className="flex flex-1 normal:flex-1 normal:flex-row flex-col normal:gap-x-1.5">
        {locationObj("pickup", {
          className: "normal:mr-1",
          placeholder: data?.content.bePickupLocLabel,
        })}
        <SwapButton onSwap={swap} y={size < 1080 ? -12 : pickup && dropoff ? 14 : 6} />
        <div className="flex flex-col self-start flex-1 w-full normal:w-auto normal:flex-1">
          {locationObj("dropoff", {
            className: "normal:mt-0 mt-3",
            placeholder: data?.content.beDropoffLocLabel,
          })}
        </div>
      </div>
      {!noMap && (
        <MapPopup
          className="absolute text-right  normal:relative normal:bottom-0 normal:text-right norm sm:right-5 right-1 -bottom-8"
          label={data?.content.beMapPopupLinkLabel}
          startPoint={pickup?.latLng}
          endPoint={dropoff?.latLng}
          visible={pickup?.latLng?.lng && dropoff?.latLng?.lng}
        />
      )}
    </motion.div>
  );
}

export default dynamic(() => Promise.resolve(PickupAndDropoff), {
  ssr: false,
});
